import { liftEntity } from "../api/mentalModels";
import { stageMmOpen } from "./mmHandoff";
import { followUpTarget } from "./nowModes";
import { mentalModelChoice, routeKey, type DeeperItem, type DeeperRoute } from "./diveDeeper";

// One handler per type (spec 2026-11-04 §3.1): inline routes open in NowToolHost,
// everything else is a hand-off that leaves the sheet.
const INLINE = new Set<DeeperRoute["route"]>(["breakdown", "guided_activity", "trait_panel", "project_step", "next_step", "question", "operator"]);

export const isInlineRoute = (r: DeeperRoute): boolean => INLINE.has(r.route);

export interface HandoffDeps {
  retryFetch: Function;
  /** Records the hand-off on the session before navigating (spec §2.4). */
  beforeLeave?: (routeKey: string) => Promise<void>;
  /** Closes the sheet and navigates. */
  leave: (path: string) => void;
}

/** Runs a hand-off choice: lift if needed, record it, stage, navigate. Throws (nothing staged) on failure. */
export async function runHandoff(route: DeeperRoute, item: DeeperItem, deps: HandoffDeps): Promise<void> {
  const before = deps.beforeLeave ?? (async () => {});
  switch (route.route) {
    case "mm_script":
    case "mm_general":
      if (item.kind !== "mental_model") throw new Error("not_a_model");
      await before(routeKey(route));
      stageMmOpen(item.id, route.route === "mm_script" ? route.pathId : undefined, { visit: true });
      deps.leave("/portal");
      return;
    case "underneath": {
      const lifted = await liftEntity(deps.retryFetch, {
        entityType: item.kind === "quality" ? "user_trait" : "user_takeaway",
        entityId: item.id,
        objectType: item.mmType,
      });
      const next = mentalModelChoice(lifted.object.objectType).recommended;
      await before("underneath");
      stageMmOpen(lifted.object._id, next.route === "mm_script" ? next.pathId : undefined, { visit: true });
      deps.leave("/portal");
      return;
    }
    case "project_room":
      await before("project_room");
      deps.leave(`/project/${item.id}`);
      return;
    case "activity":
      await before("activity");
      deps.leave(followUpTarget("activity", route.refId).path);
      return;
    default:
      throw new Error(`not_a_handoff:${route.route}`);
  }
}
